import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';


const AddDeviceModal = ({ isOpen, onClose, onAdd, supportedDevices }) => {
  const [formData, setFormData] = useState({
    deviceType: '',
    deviceName: '',
    apiKey: '',
    apiSecret: '',
    accessToken: '',
    syncedMetrics: ['heart_rate', 'steps', 'calories']
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setLoading(true);
    setError(null);

    const { data, error: addError } = await onAdd({
      ...formData,
      isActive: true,
      batteryLevel: 100,
      lastSyncAt: new Date()?.toISOString()
    });

    setLoading(false);

    if (addError) {
      setError(addError?.message || 'Failed to add device');
    } else {
      onClose();
    }
  };

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const metricOptions = [
    { value: 'heart_rate', label: 'Heart Rate' },
    { value: 'steps', label: 'Steps' },
    { value: 'calories', label: 'Calories' },
    { value: 'sleep', label: 'Sleep' },
    { value: 'workouts', label: 'Workouts' },
    { value: 'distance', label: 'Distance' }
  ];

  const toggleMetric = (metric) => {
    const current = formData?.syncedMetrics || [];
    const updated = current?.includes(metric)
      ? current?.filter(m => m !== metric)
      : [...current, metric];
    handleChange('syncedMetrics', updated);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <motion.div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />

          {/* Modal */}
          <motion.div
            className="relative bg-card rounded-xl p-6 w-full max-w-2xl elevation-4 max-h-[90vh] overflow-y-auto"
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Icon name="Plus" size={24} className="text-primary" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-foreground">Add Wearable Device</h2>
                  <p className="text-sm text-muted-foreground">Connect a new device to sync health data</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-muted rounded-lg transition-colors"
              >
                <Icon name="X" size={20} className="text-muted-foreground" />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Device Type Selection */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Device Type *
                </label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {supportedDevices?.map((device) => (
                    <button
                      key={device?.type}
                      type="button"
                      onClick={() => handleChange('deviceType', device?.type)}
                      className={`flex items-center space-x-3 p-3 rounded-lg border-2 transition-all ${
                        formData?.deviceType === device?.type
                          ? 'border-primary bg-primary/10' :'border-wellness-light hover:border-primary/50'
                      }`}
                    >
                      <div className={`p-2 rounded-lg ${device?.bgColor}`}>
                        <Icon name={device?.icon} size={18} className={device?.color} />
                      </div>
                      <span className="text-sm font-medium text-foreground">{device?.name}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Device Name */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Device Name *
                </label>
                <Input
                  type="text"
                  placeholder="e.g., My Garmin Watch"
                  value={formData?.deviceName}
                  onChange={(e) => handleChange('deviceName', e?.target?.value)}
                  required
                />
              </div>

              {/* API Credentials */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-foreground">API Credentials</h3>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    API Key *
                  </label>
                  <Input
                    type="text"
                    placeholder="Enter your API key"
                    value={formData?.apiKey}
                    onChange={(e) => handleChange('apiKey', e?.target?.value)}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    API Secret
                  </label>
                  <Input
                    type="password"
                    placeholder="Enter your API secret (if required)"
                    value={formData?.apiSecret}
                    onChange={(e) => handleChange('apiSecret', e?.target?.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Access Token
                  </label>
                  <Input
                    type="text"
                    placeholder="Enter access token (if required)"
                    value={formData?.accessToken}
                    onChange={(e) => handleChange('accessToken', e?.target?.value)}
                  />
                </div>
              </div>

              {/* Metrics to Sync */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Metrics to Sync
                </label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {metricOptions?.map((metric) => (
                    <button
                      key={metric?.value}
                      type="button"
                      onClick={() => toggleMetric(metric?.value)}
                      className={`p-3 rounded-lg border-2 text-sm font-medium transition-all ${
                        formData?.syncedMetrics?.includes(metric?.value)
                          ? 'border-primary bg-primary/10 text-primary' :'border-wellness-light text-muted-foreground hover:border-primary/50'
                      }`}
                    >
                      {metric?.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Error Message */}
              {error && (
                <div className="flex items-center space-x-2 p-3 bg-error/10 border border-error/20 rounded-lg">
                  <Icon name="AlertCircle" size={20} className="text-error" />
                  <p className="text-sm text-error">{error}</p>
                </div>
              )}

              {/* Actions */}
              <div className="flex space-x-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={onClose}
                  className="flex-1"
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1"
                  disabled={loading || !formData?.deviceType || !formData?.deviceName || !formData?.apiKey}
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Adding...
                    </>
                  ) : (
                    'Add Device'
                  )}
                </Button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default AddDeviceModal;
